import { gql, GraphQLClient } from 'graphql-request';
import { parseEther } from 'viem';

import { getContractAddress } from '~config/protocol';
import { NetworkIds } from '~config/rpcConfig';
import { EVestaAsset } from '~config/types';
import {
  getAllQuery,
  GraphTroveChange,
  queryVaultChangeState,
  transactionQuery,
} from '~services/subgraph/queries';

import { GraphUserData, GraphUserVault } from '../types';

import { troveQuery, stabilityQuery, allowancesQuery, balancesQuery } from './queries';

export default class User {
  constructor(private readonly client: GraphQLClient, private readonly networkId: NetworkIds) {}

  async getUserStats(id: string, asset: string) {
    const userStatsQuery = gql`
      query get_user_stats($asset: ID, $id: ID){
          user(id: $id) {
            collSurplus 
            ${troveQuery}
            ${stabilityQuery}
            ${allowancesQuery}
            ${balancesQuery}
        }
      }
    `;

    const res: { user: GraphUserData } = await this.client.request(userStatsQuery, { asset, id });

    return res;
  }

  async getUserVault(userAddress: string, asset: EVestaAsset, network: NetworkIds) {
    const userVaultQuery = gql`
      query get_user_vaults($address: ID, $asset: ID) {
        user(id: $address) {
          troves(where: { id: $asset }) {
            status
            collateral
            debt
            rawCollateral
            rawDebt
            changes {
              ${transactionQuery}
              collateralBefore
              collateralChange
              collateralAfter
              debtBefore
              debtChange
              debtAfter
              borrowingFee
            }
          }
        }
      }
    `;

    const {
      user: { troves },
    }: {
      user: {
        troves: {
          status: string;
          collateral: string;
          debt: string;
          rawCollateral: string;
          rawDebt: string;
        }[];
      };
    } = await this.client.request(userVaultQuery, {
      address: userAddress.toLowerCase(),
      // eslint-disable-next-line prettier/prettier
      asset: `${userAddress.toLowerCase()}-${getContractAddress(asset, network).toLowerCase()}`,
    });

    const trove = troves[0];

    return trove
      ? ({
          rawCollateral: parseEther(trove.collateral),
          rawDebt: parseEther(trove.debt),
          collateral: Number(trove.collateral),
          debt: Number(trove.debt),
        } as GraphUserVault)
      : null;
  }

  async getUserTotalDebt(id: string) {
    const userDebtQuery = gql`
      query get_user_total_debt($id: ID) {
        user(id: $id) {
          totalDebt
        }
      }
    `;

    const res: { user?: { totalDebt?: string } } = await this.client.request(userDebtQuery, {
      id: id.toLowerCase(),
    });

    return Number(res?.user?.totalDebt || 0);
  }

  async getAllUserVaultChanges(userAddress: string) {
    return await getAllQuery<{ asset: { name: EVestaAsset }; changes: GraphTroveChange[] }>({
      query: queryVaultChangeState,
      queryParams: {
        userAddress: userAddress.toLowerCase(),
      },
      client: this.client,
      combineKey: 'troves',
    });
  }
}
